import React, { useCallback, useMemo, useState } from "react";
import { Typography, colors } from "@commonsku/styles";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { BASE_COLUMN_TYPES } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { useReportContext } from "../../report_context";
import { formatDateFromString } from "../../../../utils";
import CreditMemoPopup from "../../../credit-memo/CreditMemoPopup";
import VendorCreditPopup from "../../../vendor-credit/VendorCreditPopup";
import { Bill } from "../../../../types/bills";
import { CreditMemo } from "../../../../types/credit_memo";
import { VendorCredit } from "../../../../types/vendor_credit";
import { retrieveBill } from "../../../../redux/bills";
import { retrieveCreditMemo } from "../../../../redux/credit_memos";
import { retrieveVendorCredit } from "../../../../redux/vendor_credits";
import BillPopup from "../../../bills/BillPopup";

const SUBHEADER_STYLE = {
  fontWeight: "normal",
  fontSize: "12px",
  fontFamily: "skufont-regular, sans-serif"
};

const ProjectLink = styled.a`
  color: ${colors.primary1.main};
  font-family: skufont-regular, sans-serif;
`;

export const DateColumnHeader = ({ name, headerTitle }) => {
  const { summaryData } = useReportContext();
  const summary = summaryData.find(d => d.label === name);
  if (!summary) {
    return name;
  }

  const startDate = formatDateFromString(summary.data[0], { year: "numeric", month: "short", day: "numeric", locales: "en-US" });
  const endDate = formatDateFromString(summary.data[1], { year: "numeric", month: "short", day: "numeric", locales: "en-US" });

  const subheader = startDate && endDate
    ? `${startDate} - ${endDate}`
    : startDate
      ? "Since " + startDate
      : endDate
        ? "Until " + endDate
        : "All";

  return (
    <div title={name}>
      <div>{headerTitle}</div>
      <div style={SUBHEADER_STYLE}>
        {subheader}
      </div>
    </div>
  );
};

export const OpportunityNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Opportunity",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["order_id", "job_number", "form_number"],
  accessorKey: "opportunity_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}`,
    text: form_number,
  }),
};

export const PresentationNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Presentation&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["order_id", "job_number", "form_number"],
  accessorKey: "presentation_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}/presentation/${form_number}`,
    text: form_number,
  }),
};

export const EstimateNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Estimate&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["order_id", "job_number", "form_number"],
  accessorKey: "estimate_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}/estimate/${form_number}`,
    text: form_number,
  }),
};

export const SalesOrderNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Sales&nbsp;Order&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["order_id", "job_number", "form_number"],
  accessorKey: "sales_order_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}/sales-order/${form_number}`,
    text: form_number,
  }),
};

export const InvoiceNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Invoice&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["order_id", "job_number", "form_number"],
  accessorKey: "invoice_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}/invoice/${form_number}`,
    text: form_number,
  }),
};

export const PurchaseOrderNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>PO&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["purchase_order_id", "job_number", "form_number"],
  accessorKey: "purchase_order_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ job_number, form_number }) => ({
    href: `/project/${job_number}/production`,
    text: form_number,
  }),
};

export const ClientColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Client",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["client_id", "client_name"],
  accessorKey: "client",
  canSort: true,
  orderByField: "client_name",
  width: 250,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ client_id, client_name }) => ({
    href: `/client.php?id=${client_id}`,
    text: client_name,
  }),
  // linkAction: ({ client_id }) => console.log(client_id), // change to open clientSidePanel
};

export const ParentClientColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Client",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["parent_id", "parent_name"],
  accessorKey: "client",
  canSort: true,
  orderByField: "parent_name",
  width: 250,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ parent_id, parent_name }) => ({
    href: `/parent.php?id=${parent_id}`,
    text: parent_name,
  }),
  // linkAction: ({ client_id }) => console.log(client_id), // change to open clientSidePanel
};

export const SupplierColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Supplier",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["division_name", "supplier_id", "division_id"],
  accessorKey: "division_name",
  canSort: true,
  orderByField: "division_name",
  width: 344,
  dataTransform: ({ division_name, supplier_id, division_id }) => ({
    text: division_name,
    href: `/vendor.php?id=${supplier_id}&division_id=${division_id}`,
  }),
};

export const ProjectColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number", "job_name"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 250,
  dataTransform: ({ job_number, job_name }) => ({
    href: !!job_number ? `/project/${job_number}` : null,
    text: !!job_number ? `${job_number}: ${job_name}` : '—',
  }),
};

export const ProjectNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Project&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 83,
  justify: "center",
  dataTransform: ({ job_number }) => ({
    href: job_number ? `/project/${job_number}` : "",
    text: job_number ?? "\u2014",
  }),
};

export const MultiProjectNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: <>Project&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["jobs", "purchase_orders"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 120,
  justify: "center",
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ jobs, purchase_orders }) => {
    // Prefer jobs if available, fall back to purchase_orders
    const sourceData = jobs && jobs.length > 0 ? jobs : purchase_orders;

    if (!sourceData || sourceData.length === 0) {
      return {
        cell: "\u2014",
      };
    }

    // Group by job_number to get unique projects with their details
    const projectMap = new Map<string, { job_number: string; job_name?: string; job_id?: string }>();
    sourceData.forEach((item: { job_number: string; job_name?: string; job_id?: string }) => {
      if (item.job_number && !projectMap.has(item.job_number)) {
        projectMap.set(item.job_number, {
          job_number: item.job_number,
          job_name: item.job_name,
          job_id: item.job_id,
        });
      }
    });

    const projects = Array.from(projectMap.values());

    if (projects.length === 0) {
      return {
        cell: "\u2014",
      };
    }

    if (projects.length === 1) {
      return {
        cell: (
          <ProjectLink href={`/project/${projects[0].job_number}`}>
            {projects[0].job_number}
          </ProjectLink>
        ),
      };
    }

    return {
      cell: projects.map(p => p.job_number).join(", "),
      tooltipText: (
        <div>
          <p>Projects:</p>
          {projects.map((project) => (
            <Typography.Span
              key={project.job_id || project.job_number}
              as="a"
              style={{
                display: "block",
                alignContent: "left",
                color: colors.primary1.main,
              }}
              href={`/project/${project.job_number}`}
              target="_blank"
              rel="noreferrer"
              title={project.job_name}
            >
              #{project.job_number}{project.job_name ? `: ${project.job_name}` : ""}
            </Typography.Span>
          ))}
        </div>
      ),
      tooltipOptions: { effect: "solid", place: "right" },
    };
  },
};

export const BudgetColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: "Budget",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_budget", "currency_id"],
  accessorKey: "budget",
  canSort: true,
  orderByField: "total_budget",
  width: 95,
  moneyOptions: { decimalPlaces: 0 },
  dataTransform: ({ total_budget, currency_id }) => ({
    amount: total_budget,
    currency_id,
  }),
};

export const SubtotalColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: "Subtotal",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_subtotal", "currency_id"],
  accessorKey: "subtotal",
  canSort: true,
  orderByField: "total_subtotal",
  width: 95,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_subtotal, currency_id }) => ({
    amount: total_subtotal,
    currency_id,
  }),
};

export const TotalColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: "Total",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_total", "currency_id"],
  accessorKey: "total",
  canSort: true,
  orderByField: "total_total",
  width: 95,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_total, currency_id }) => ({
    amount: total_total,
    currency_id,
  }),
};

export const OrderDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Order&nbsp;Date</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_orderdate"],
  accessorKey: "date_orderdate",
  canSort: true,
  orderByField: "date_orderdate",
  width: 120,
};

export const DateCreatedColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Date&nbsp;Created</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_created"],
  accessorKey: "date_created",
  canSort: true,
  orderByField: "date_created",
  width: 120,
};

export const BillPurchaseOrdersColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Purchase&nbsp;Orders</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["purchase_orders", "division_id"],
  accessorKey: "purchase_orders",
  canSort: true,
  orderByField: "purchase_orders",
  width: 105,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ purchase_orders, division_id }) => ({
    text: purchase_orders.map(po => po.po_number).join(", "),
    href: purchase_orders.length === 1 ? `/purchase_order.php?id=${purchase_orders[0].purchase_order_id}` : null,
    tooltipText: (
      <div>
        <p>Purchase Orders in this bill:</p>
        {purchase_orders.map((po, index) => (
          <React.Fragment key={po.purchase_order_id}>
            <Typography.Span
              as="a"
              style={{
                display: "block",
                alignContent: "left",
                color: colors.primary1.main,
              }}
              href={`/purchase_order.php?id=${po.purchase_order_id}`}
              target="_blank"
              rel="noreferrer"
              title={po.po_name}
            >
              #{po.po_number} {division_id !== po.division_id && <>({po.division_name})</>}
            </Typography.Span>
          </React.Fragment>
        ))}
      </div>
    ),
    tooltipOptions: { effect: "solid", place: "right" }
  })
};

function DetailPopup<T>(
  {
    id,
    number,
    retrieve,
    createPopup
  }: {
    id: string,
    number: string,
    retrieve: (id: string) => (dispatch: ReturnType<typeof useDispatch>) => Promise<T>,
    createPopup: (detail: T, onClose: (saved: boolean) => void) => React.ReactNode
  }
) {
  const [detail, setDetail] = useState<T | undefined>();
  const dispatch = useDispatch();
  const { fetchReport } = useReportContext();

  const handleClick = useCallback(async () => {
    const detail = await retrieve(id)(dispatch);
    setDetail(detail);
  }, [id, dispatch]);

  const handleClose = useCallback(
    (saved: boolean) => {
      if (saved) {
        fetchReport();
      }
      setDetail(undefined);
    },
    [detail, fetchReport]
  );

  const popup = useMemo(
    () => {
      if (detail) {
        return createPopup(detail, handleClose);
      }
      return null;
    },
    [detail, createPopup, handleClose]
  );

  return (
    <>
      {popup}
      <Typography.Span
        as="a"
        style={{
          display: "block",
          alignContent: "center",
          color: colors.primary1.main,
        }}
        title={number}
        onClick={handleClick}
      >
        {number}
      </Typography.Span>
    </>
  );
}

export const VendorCreditNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: <>Vendor&nbsp;Credit</>,
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["vendor_credit_id", "form_number"],
  accessorKey: "form_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  justify: "center",
  dataTransform: ({ vendor_credit_id, form_number }) => {
    const createPopup = (detail: VendorCredit, onClose: (saved: boolean) => void) => (
      <VendorCreditPopup vendorCredit={detail} onClose={onClose} />
    );

    return {
      cell: (
        <DetailPopup<VendorCredit>
          id={vendor_credit_id}
          number={form_number}
          retrieve={retrieveVendorCredit}
          createPopup={createPopup}
        />
      )
    };
  },
};

export const CreditMemoNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: <>Credit&nbsp;Memo</>,
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["credit_memo_id", "form_number"],
  accessorKey: "form_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  justify: "center",
  dataTransform: ({ credit_memo_id, form_number }) => {
    const createPopup = (detail: CreditMemo, onClose: (saved: boolean) => void) => (
      <CreditMemoPopup creditMemo={detail} onClose={onClose} />
    );

    return {
      cell: (
        <DetailPopup<CreditMemo>
          id={credit_memo_id}
          number={form_number}
          retrieve={retrieveCreditMemo}
          createPopup={createPopup}
        />
      )
    };
  },
};

export const BillReferenceNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: <>Bill&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["bill_id", "bill_reference_number"],
  accessorKey: "bill_reference_number",
  canSort: true,
  orderByField: "bill_reference_number",
  width: 83,
  dataTransform: ({ bill_id, bill_reference_number }) => {
    const createPopup = (detail: Bill, onClose: (saved: boolean) => void) => (
      <BillPopup bill={detail} onClose={onClose} />
    );

    return {
      cell: (
        <DetailPopup<Bill>
          id={bill_id}
          number={bill_reference_number}
          retrieve={retrieveBill}
          createPopup={createPopup}
        />
      )
    };
  },
};

export const StatusColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Status> = {
  header: "Form",
  type: BASE_COLUMN_TYPES.Status,
  fields: ["order_type", "status_name", "form_number", "job_number"],
  accessorKey: "form",
  canSort: true,
  orderByField: "form_number",
  width: 177,
  dataTransform: ({ order_type, status_name, form_number, job_number }) => ({
    order_type,
    form_number,
    status_name,
    href: `/project/${job_number}${order_type === "OPPORTUNITY"
      ? ""
      : `/${order_type
        .split(" ")
        .map((o: string) => o.toLowerCase())
        .join("-")}/${form_number}`
      } `,
  }),
};